import * as R from 'ramda';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { apiSaga } from '../../../../root-sagas';
import {
  contracting as conApi,
  workspace as wpApi,
  partner as partnerApi,
  partnerManagement as partnerManagementApi,
  ti2 as ti2Api,
} from '../../../../api';
import * as cnt from './constants.js';
import { updatePartnerDetails } from './actions';
import {
  buildLocalValidationError,
  hasMissingPartnerHintLabels,
  hasResolvablePartnerHints,
  normalizeMappingForSave,
} from '../mapping-utils';

const PARTNER_LOOKUP_SEARCH_PARAMS = {
  listBy: 'partner',
  summaryOnly: true,
};

const getRoute = state => state.router.location.pathname;
const getCompanyId = state => R.path(['account', 'company', 'companyId'], state);
const getTi2Token = state => R.path(['account', 'travelMLSettings', 'ti2', 'token'], state);
const getAllPartners = state => R.pathOr([], ['PM', 'partnerList', 'list', 'partners'], state);
const getMappingDialog = state => R.pathOr({}, ['PM', 'partnerDetails', 'mappingDialog'], state);
const getPartnerInfo = state => R.pathOr({}, ['PM', 'partnerDetails', 'partnerInfo'], state);

export function* refreshPartnerInfo() {
  const pathname = yield select(getRoute);
  if (pathname.split('/')[2] !== 'partner-details') return;
  const partnerId = pathname.split('/')[3];
  if (!partnerId) return;
  yield apiSaga(cnt.GET_PARTNER_INFO, partnerApi.getPartner, [{ partnerId }]);
}

function* getPartnerDetails({
  payload: {
    location: { pathname },
  },
}) {
  if (pathname.split('/')[2] === 'partner-details') {
    const partnerId = pathname.split('/')[3];
    yield put({ type: cnt.CLEAR_STORE });

    yield all([
      apiSaga(cnt.GET_PARTNER_INFO, partnerApi.getPartner, [{ partnerId }]),
      _getFiles(partnerId),
      _getActivities(partnerId),
    ]);
  }
}

function* doAddANote({ payload }) {
  yield apiSaga('@partner_contact_details/ADD_A_NOTE', wpApi.addANote, [payload]);
}

export function* refreshActivities() {
  const pathname = yield select(getRoute);
  if (pathname.split('/')[2] === 'partner-details') {
    const companyId = pathname.split('/')[3];
    yield apiSaga(cnt.GET_ACTIVITY, partnerApi.getActivities, [
      { assetId: companyId },
    ]);
  }
}

function* _getActivities(partnerId) {
  yield apiSaga(cnt.GET_ACTIVITY, partnerApi.getActivities, [
    { assetId: partnerId },
  ]);
}

function* _getFiles(partnerId) {
  yield apiSaga(cnt.GET_FILES, conApi.getContracts, [
    {
      query: { partnerIds: partnerId },
      includeWorkspacesIfBuyer: true,
    },
  ]);
}

function* markAsRead() {
  const pathname = yield select(getRoute);
  if (pathname.split('/')[2] === 'partner-details') {
    const partnerId = pathname.split('/')[3];
    yield apiSaga(cnt.CONVERSATIONS_READ, wpApi.conversationsRead, [
      { assetId: partnerId, assetType: 'partner' },
    ]);
  }
}

function* doUpdatePartnerDetails({ type, payload }) {
  const pathname = yield select(getRoute);
  const partnerIdFromRoute = pathname.split('/')[2] === 'partner-details'
    ? pathname.split('/')[3]
    : undefined;
  const partnerIdFromStore = yield select(
    state => state.PM.partnerDetails.partnerInfo.id
  );
  const { partnerId: partnerIdFromPayload, ...nextPayload } = payload || {};
  const partnerId = partnerIdFromPayload || partnerIdFromRoute || partnerIdFromStore;
  if (!partnerId) return;
  yield apiSaga(type, partnerApi.updatePartnerDetails, [
    {
      ...nextPayload,
      partnerId,
    },
  ]);
}

function* doUnassocMsg({ payload }) {
  const confirm = window.confirm(
    'Are you sure you want to remove this activity from this partner?'
  );
  if (confirm) {
    const assetId = yield select(
      state => state.PM.partnerDetails.partnerInfo.id
    );
    yield apiSaga(cnt.UNASSOC_A_MESSAGE, partnerApi.unassocAMessage, [
      { messageId: payload.messageId, assetId },
    ]);
  }
}

export function* ensureLookupPartners({ force = false } = {}) {
  const allPartners = yield select(getAllPartners);
  const mappingDialog = yield select(getMappingDialog);
  if (mappingDialog.lookupPartnersLoading) return;
  if (mappingDialog.lookupPartners.length > 0) return;

  const ti2AgentMappings = R.pathOr([], ['misc', 'ti2Agents'], yield select(getPartnerInfo));
  if (!force && !hasResolvablePartnerHints(ti2AgentMappings)) return;
  if (!force && !hasMissingPartnerHintLabels(ti2AgentMappings, allPartners)) return;

  yield apiSaga(
    cnt.LOAD_MAPPING_LOOKUP_PARTNERS,
    partnerManagementApi.getPartners,
    [PARTNER_LOOKUP_SEARCH_PARAMS]
  );
}

export function* onGetPartnerInfoSuccess() {
  yield call(ensureLookupPartners);
}

export function* onOpenMappingDialog() {
  const companyId = yield select(getCompanyId);
  const ti2token = yield select(getTi2Token);

  if (companyId && ti2token) {
    yield apiSaga(
      cnt.LOAD_MAPPING_APP_KEYS,
      ti2Api.getAllAppKeys,
      [{ companyId, ti2token }]
    );
  }

  yield call(ensureLookupPartners, { force: true });
}

export function* onSaveMappingDialog() {
  const {
    index,
    values,
  } = yield select(getMappingDialog);
  const partnerMisc = R.pathOr({}, ['misc'], yield select(getPartnerInfo));
  const ti2AgentMappings = Array.isArray(partnerMisc.ti2Agents)
    ? partnerMisc.ti2Agents
    : [];

  try {
    const normalizedMapping = normalizeMappingForSave(values);
    const localValidationError = buildLocalValidationError({
      mapping: normalizedMapping,
      mappings: ti2AgentMappings,
      editingIndex: index,
    });

    if (localValidationError) {
      yield put({ type: cnt.SET_MAPPING_DIALOG_ERROR, payload: localValidationError });
      return;
    }

    const nextMappings = [...ti2AgentMappings];
    if (typeof index === 'number' && index > -1) {
      nextMappings[index] = normalizedMapping;
    } else {
      nextMappings.push(normalizedMapping);
    }

    yield put({ type: cnt.SET_MAPPING_DIALOG_SAVE_PENDING, payload: true });
    yield put(updatePartnerDetails({
      updatedDefaultData: {
        misc: {
          ...partnerMisc,
          ti2Agents: nextMappings,
        },
      },
      updatedCustomizedData: [],
    }));
  } catch (validationError) {
    yield put({
      type: cnt.SET_MAPPING_DIALOG_ERROR,
      payload: validationError.message,
    });
  }
}

const sagas = [
  takeLatest('@@router/LOCATION_CHANGE', getPartnerDetails),
  takeLatest('@partner_contact_details/ADD_A_NOTE', doAddANote),
  takeLatest('@partner_contact_details/ADD_A_NOTE_SUCCESS', refreshActivities),
  takeLatest(`${cnt.GET_ACTIVITY}_SUCCESS`, markAsRead),
  takeLatest(cnt.UPDATE_PARTNER_DETAILS, doUpdatePartnerDetails),
  takeLatest(`${cnt.GET_PARTNER_INFO}_SUCCESS`, onGetPartnerInfoSuccess),
  takeLatest(cnt.OPEN_MAPPING_DIALOG, onOpenMappingDialog),
  takeLatest(cnt.SAVE_MAPPING_DIALOG, onSaveMappingDialog),
  takeLatest(`${cnt.UPDATE_PARTNER_DETAILS}_SUCCESS`, refreshPartnerInfo),
  takeLatest(cnt.UNASSOC_A_MESSAGE, doUnassocMsg),
  takeLatest([
    `${cnt.UNASSOC_A_MESSAGE}_SUCCESS`,
    `${cnt.UPDATE_PARTNER_DETAILS}_SUCCESS`,
  ], refreshActivities),
];

export default sagas;
